import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, InputAdornment, Paper, Typography, Chip, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../utils/api";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };

// Reusable HOA-member/property search-and-select, extracted from the pattern
// already used in Marina/MarinaVessels.js. Calls onSelect(member) with the
// full /membersfilter row ({ ClubMemberCode, FirstName, LastName, FullAddress, ... }).
const MemberPicker = ({ label = "Search HOA Member *", selectedMember, onSelect, onClear }) => {
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!search.trim()) { setMembers([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/membersfilter?search=${encodeURIComponent(search)}`);
        setMembers(res.data?.members || []);
      } catch { /* silent */ }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  if (selectedMember) {
    return (
      <Box sx={{ p: 1.5, borderRadius: "10px", backgroundColor: "rgba(47,99,173,0.05)", border: "1px solid rgba(47,99,173,0.2)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
              {selectedMember.FirstName} {selectedMember.LastName}
            </Typography>
            <Chip label={`#${selectedMember.ClubMemberCode}`} size="small"
              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, fontFamily: "monospace",
                backgroundColor: "rgba(47,99,173,0.12)", color: "#2f63ad" }} />
          </Box>
          {selectedMember.FullAddress && (
            <Typography variant="caption" sx={{ color: "#6b778c" }}>{selectedMember.FullAddress}</Typography>
          )}
        </Box>
        <Button size="small" variant="outlined"
          onClick={() => { setSearch(""); onClear(); }}
          sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.75rem", whiteSpace: "nowrap", flexShrink: 0 }}>
          Change
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <TextField fullWidth label={label} placeholder="Name, member code, or address…"
        value={search} onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9aa5b4" }} /></InputAdornment> }}
        sx={fieldSx} />
      {members.length > 0 && (
        <Paper elevation={3} sx={{ mt: 0.5, borderRadius: "10px", overflow: "hidden", maxHeight: 240, overflowY: "auto" }}>
          {members.map((m) => (
            <Box key={m.ClubMemberCode}
              onClick={() => { onSelect(m); setSearch(""); setMembers([]); }}
              sx={{ px: 2, py: 1, cursor: "pointer", borderBottom: "1px solid #f0f0f0",
                "&:hover": { backgroundColor: "rgba(47,99,173,0.05)" } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
                  {m.FirstName} {m.LastName}
                </Typography>
                <Chip label={`#${m.ClubMemberCode}`} size="small"
                  sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, fontFamily: "monospace",
                    backgroundColor: "rgba(47,99,173,0.1)", color: "#2f63ad", flexShrink: 0 }} />
              </Box>
              {m.FullAddress && (
                <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{m.FullAddress}</Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default MemberPicker;
